import { ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
/**
 * SafetyDashboardPreview — tiny inline SVG mockup of an AI message-scanning
 * dashboard. Shows three message bubbles being scanned in real-time with
 * animated traffic-light indicators (green=safe, amber=caution, red=blocked).
 * Pure SVG + Tailwind, ~2KB, no images.
 */
const SafetyDashboardPreview = ({ language = 'ar', isDark = false }) => {
  const { localize } = useLanguage();
  const isAr = language === 'ar';
  return (
    <div
      className={`mt-3 relative rounded-xl overflow-hidden border ${
        isDark ? 'border-white/10 bg-[#0d1117]' : 'border-slate-200 bg-white'
      }`}
      data-testid="safety-dashboard-preview"
      aria-label={localize({ ar: 'معاينة لوحة الأمان', en: 'Safety dashboard preview' })}
    >
      {/* Header strip */}
      <div className={`flex items-center justify-between px-2.5 py-1.5 border-b ${isDark ? 'border-white/5 bg-white/[0.02]' : 'border-slate-100 bg-slate-50'}`}>
        <div className="inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-rose-500" />
          <span className={`text-[9px] sm:text-[10px] font-bold ${isDark ? 'text-slate-200' : 'text-slate-700'}`}>
            {localize({ ar: 'فحص الرسائل بالذكاء الاصطناعي', en: 'AI Message Scanner' })}
          </span>
        </div>
        <span className="inline-flex items-center gap-1 text-[8px] sm:text-[9px] font-bold text-emerald-600 dark:text-emerald-400">
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          {localize({ ar: 'مباشر', en: 'LIVE' })}
        </span>
      </div>

      {/* Message rows */}
      <div className="px-2.5 py-2 space-y-1.5">
        {[
          { color: 'emerald', icon: CheckCircle2, ar: 'سؤال عن الواجب', en: 'Homework question', delay: 0 },
          { color: 'amber',   icon: AlertTriangle, ar: 'تحقّق من السياق', en: 'Context check',   delay: 0.4 },
          { color: 'emerald', icon: CheckCircle2, ar: 'محتوى تعليمي آمن', en: 'Educational',     delay: 0.8 },
        ].map((row, i) => {
          const RowIcon = row.icon;
          const palette = {
            emerald: { bar: 'bg-emerald-400', text: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
            amber:   { bar: 'bg-amber-400',   text: 'text-amber-600 dark:text-amber-400',     bg: 'bg-amber-50 dark:bg-amber-900/20' },
            red:     { bar: 'bg-rose-400',    text: 'text-rose-600 dark:text-rose-400',       bg: 'bg-rose-50 dark:bg-rose-900/20' },
          }[row.color];

          return (
            <div key={i} className="flex items-center gap-2">
              {/* Status dot + icon */}
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${palette.bg} ${palette.text} shrink-0`}>
                <RowIcon className="w-2.5 h-2.5" />
              </span>

              {/* Message label */}
              <span className={`text-[9px] sm:text-[10px] font-medium truncate flex-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                {localize({ ar: row.ar, en: row.en })}
              </span>

              {/* Animated scan bar */}
              <div className={`relative w-12 sm:w-16 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                <span
                  className={`absolute inset-y-0 left-0 ${palette.bar} rounded-full`}
                  style={{
                    animation: `lwt-scan-${i} 2.4s ease-in-out ${row.delay}s infinite`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Inline keyframes — scoped per row to avoid sync */}
      <style>{`
        @keyframes lwt-scan-0 { 0%,100% { width: 70%; } 50% { width: 95%; } }
        @keyframes lwt-scan-1 { 0%,100% { width: 50%; } 50% { width: 80%; } }
        @keyframes lwt-scan-2 { 0%,100% { width: 85%; } 50% { width: 65%; } }
      `}</style>
    </div>
  );
};

export default SafetyDashboardPreview;
